//Deep links into a Page Builder page, e.g. /bsm2026#tue-0930 or /bsm2026#session-2952.
//
//Two kinds of anchor:
//  - an element's optional "anchor" field (element.details.anchor), set in the builder;
//    ElementViewer renders it as the id of the element's wrapper;
//  - every session tile gets `session-<conference id>` automatically (Conferences.js),
//    so a single session can be linked without any setup. Conference ids survive page
//    duplicates and url swaps; page ids do not, which is why links never name a tab.
//
//Opt-in by construction: no anchor = no id, and a url without a #hash runs none of this.
//Nothing is stored anywhere new: the anchor lives in the page JSON the builder already saves.

//Clearance for the fixed site navbar when scrolling to an anchor.
export const ANCHOR_SCROLL_MARGIN_PX = 96;
//A deep link opens behind the app's "S" loading cover (PageViewer.js) and the cover lifts
//only once the page is at the anchor, so the visitor never sees it move (Omerson 09/24/2026:
//keep the loader "until the scroll to anchor is 100 percent ready", then an INSTANT jump).
//This is the most the cover may stay up, counted from when the page started loading: a slow
//ruleset or tile request must never leave a visitor looking at a spinner.
export const ANCHOR_COVER_MAX_MS = 9000;
//Tiles load in their own request after the ruleset check (up to ~15 s in the worst case),
//so a tile anchor can appear well after the page itself. Past the cover, keep looking this long.
const REVEAL_TIMEOUT_MS = 20000;
const POLL_MS = 100;
//The target counts as settled once nothing above it is still loading AND it has sat at the
//same place for this many polls in a row (rows above load their tiles and push it down).
const STABLE_POLLS = 3;
//Without a cover (a #hash changed on a page already open) never wait longer than this.
const MAX_SETTLE_UNCOVERED_MS = 2500;
//One safety check after the jump: jump again only if something above still moved it.
const RECHECK_AFTER_MS = 1500;
const RECHECK_TOLERANCE_PX = 4;
//Set by a tile row (Conferences.js) while it is still fetching its sessions.
export const LOADING_ATTR = 'data-pb-loading';

export const TAB_REVEAL_EVENT = 'pb:reveal-tab';

//"Tue 09:30" -> "tue-09-30". Letters, digits, - and _ only, lower case, and it must start
//with a letter so it is a valid, selector-safe id. Anything else yields undefined (no id).
export const toAnchorId = (raw) => {
  if (raw === undefined || raw === null) return undefined;
  const id = String(raw)
    .trim()
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  return /^[a-z][a-z0-9_-]*$/.test(id) ? id : undefined;
};

export const sessionAnchorId = (conferenceId) =>
  conferenceId === undefined || conferenceId === null || conferenceId === ''
    ? undefined
    : toAnchorId(`session-${conferenceId}`);

export const hashAnchorId = (hash) => {
  let value = hash === undefined ? window.location.hash : hash;
  if (!value) return undefined;
  try {
    value = decodeURIComponent(value);
  } catch (e) {
    //a malformed escape is not an anchor we could have written
    return undefined;
  }
  return toAnchorId(value.replace(/^#/, ''));
};

const isShown = (el) => el.getClientRects().length > 0;

const jumpTo = (el) => {
  el.style.scrollMarginTop = `${ANCHOR_SCROLL_MARGIN_PX}px`;
  el.scrollIntoView({ block: 'start', behavior: 'auto' });
};

//Where the target sits in the document (not the window), to tell when it has stopped moving.
const docTop = (el) =>
  Math.round(el.getBoundingClientRect().top + window.scrollY);

//Something above the target that is still loading and can still push it down: a tile row
//fetching its sessions, or an eagerly loaded picture that has not arrived yet.
const stillLoadingAbove = (el) => {
  const top = el.getBoundingClientRect().top;
  const above = (node) =>
    isShown(node) && node.getBoundingClientRect().top < top;
  for (const node of document.querySelectorAll(`[${LOADING_ATTR}]`)) {
    if (above(node)) return true;
  }
  for (const img of Array.from(document.images)) {
    if (img.complete || img.loading === 'lazy') continue;
    if (above(img)) return true;
  }
  return false;
};

//Finds the element for `id`, opens the day tab that holds it if needed, waits until it is
//in its final place, then jumps there instantly, once. Returns a cancel function.
//
//options.coverDeadline (a Date.now() time): the page is under the loading cover until then,
//so the visitor cannot see it and their scrolling does not count; the jump happens once the
//target has settled, or at the deadline at the latest. options.onSettled() is called exactly
//once, right after the jump (or when there is nothing to jump to), which is when the cover
//may lift. Without a cover the visitor's first scroll or click stops everything: we never
//fight the visitor for the scroll position.
export const revealAnchor = (id, options = {}) => {
  const { coverDeadline, onSettled } = options;
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    if (onSettled) onSettled();
  };
  if (!id) {
    settle();
    return () => {};
  }
  const covered = () => !settled && !!coverDeadline;
  let cancelled = false;
  let userMoved = false;
  let jumpedEl = null;
  let waitingForVisible = false;
  const timers = [];
  const markMoved = () => {
    if (!covered()) userMoved = true;
  };
  const moveEvents = ['wheel', 'touchmove', 'keydown', 'mousedown'];
  moveEvents.forEach((e) =>
    window.addEventListener(e, markMoved, { passive: true })
  );
  const cleanup = () => {
    cancelled = true;
    timers.forEach(clearTimeout);
    moveEvents.forEach((e) => window.removeEventListener(e, markMoved));
    document.removeEventListener('visibilitychange', onVisible);
  };
  const later = (fn, ms) => timers.push(setTimeout(fn, ms));
  //exactly one poll pending at a time (the visibility catch-up below also polls)
  let pollTimer = null;
  const nextPoll = () => {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(poll, POLL_MS);
    timers.push(pollTimer);
  };

  //after the jump: correct it once if content above still moved the target
  const recheck = () => {
    if (cancelled) return;
    if (document.visibilityState === 'hidden') {
      waitingForVisible = true;
      return;
    }
    const el = jumpedEl;
    if (!userMoved && isShown(el)) {
      const off = el.getBoundingClientRect().top - ANCHOR_SCROLL_MARGIN_PX;
      if (Math.abs(off) > RECHECK_TOLERANCE_PX) jumpTo(el);
    }
    cleanup();
  };

  const startedAt = Date.now();
  let askedForTab = false;
  let shownAt = null;
  let lastTop = null;
  let stableCount = 0;
  const poll = () => {
    if (cancelled || jumpedEl) return;
    const now = Date.now();
    const pastCover = !!coverDeadline && now >= coverDeadline;
    const el = document.getElementById(id);
    if (el && !isShown(el) && !askedForTab) {
      //it sits in a day tab that is not the open one: ask that tab to open
      const pane = el.closest('[data-pb-tab-key]');
      if (pane) {
        askedForTab = true;
        window.dispatchEvent(
          new CustomEvent(TAB_REVEAL_EVENT, {
            detail: { key: pane.getAttribute('data-pb-tab-key') }
          })
        );
      }
    }
    if (!el || !isShown(el)) {
      //not there yet: lift the cover at the deadline and keep looking behind it
      if (pastCover) settle();
      if (userMoved || now - startedAt > REVEAL_TIMEOUT_MS) {
        settle();
        return cleanup();
      }
      return nextPoll();
    }
    if (userMoved) {
      settle();
      return cleanup();
    }
    if (shownAt === null) shownAt = now;
    const top = docTop(el);
    stableCount = top === lastTop ? stableCount + 1 : 0;
    lastTop = top;
    const ready = stableCount >= STABLE_POLLS && !stillLoadingAbove(el);
    const outOfTime = coverDeadline
      ? pastCover || settled
      : now - shownAt >= MAX_SETTLE_UNCOVERED_MS;
    if (!ready && !outOfTime) return nextPoll();
    jumpTo(el);
    jumpedEl = el;
    settle();
    later(recheck, RECHECK_AFTER_MS);
  };
  //A background tab (a link opened with Ctrl+click) runs its timers about once a second;
  //catch up the moment the visitor switches to it.
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || cancelled) return;
    if (!jumpedEl) {
      clearTimeout(pollTimer);
      poll();
    } else if (waitingForVisible) recheck();
  };
  document.addEventListener('visibilitychange', onVisible);
  poll();
  return cleanup;
};
